import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { formatApiError, mediaUrl } from "@/lib/api";

export default function StaffLogin() {
  const { login, user } = useAuth();
  const { content: c } = useContent();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  useEffect(() => { if (user) navigate("/staff/dashboard"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email, password);
      navigate("/staff/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="staff-login-page" className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src={mediaUrl(c.staff_login_image)} alt="" className="img-graded absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <Link to="/"><img src="/logo.png" alt="DonoDidio Event Management" className="h-14 w-auto" /></Link>
          <div>
            <h2 className="font-serif-lux text-4xl font-light leading-tight">Staff &amp;<br />Admin Portal</h2>
            <p className="text-white/70 mt-4 max-w-sm">Manage events, shifts, gallery and enquiries — all in one place.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[#FAFAF7]">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm">
          <p className="overline mb-4">Welcome Back</p>
          <h1 className="font-serif-lux text-4xl font-light mb-10">Sign in</h1>

          {error && <div data-testid="login-error" className="mb-6 border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}

          <div className="space-y-7">
            <div>
              <label className="label-lux block mb-2">Email</label>
              <input data-testid="login-email" type="email" required className="input-lux" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@donodidioevents.co.uk" />
            </div>
            <div>
              <label className="label-lux block mb-2">Password</label>
              <input data-testid="login-password" type="password" required className="input-lux" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
          </div>

          <button type="button" onClick={() => setForgot((v) => !v)} data-testid="forgot-password-link" className="block w-full text-right text-xs text-[#8a8a80] hover:text-[#D4AF37] mt-4 transition-colors">
            Forgot password?
          </button>
          {forgot && (
            <p data-testid="forgot-password-msg" className="mt-3 text-sm text-[#4A4A4A] bg-[#F1EFE8] border border-[#E5E5DF] px-4 py-3">
              Please contact your administrator at{" "}
              <a href="mailto:info.donodidioevents@gmail.com" className="text-[#B8962E] underline">info.donodidioevents@gmail.com</a>{" "}
              to reset your password.
            </p>
          )}

          <button type="submit" disabled={busy} data-testid="login-submit-btn" className="btn-gold w-full mt-6 disabled:opacity-60">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <Link to="/" className="block text-center text-xs uppercase tracking-[0.2em] text-[#8a8a80] hover:text-[#D4AF37] mt-8">← Back to website</Link>
        </form>
      </div>
    </div>
  );
}
